import {FC, useState, useEffect, useMemo, useCallback} from 'react';
import { Channel } from '@common/channels/channel';

import { TextField } from '@ui/forms/input-field/text-field/text-field';
import { GenresIcon } from '@player/custom/icons/GenresIcon';

import { DialogTrigger } from '@ui/overlays/dialog/dialog-trigger';
import { GenresList } from '@player/custom/filters';

import { NormalizedModel } from '@player/types';
import { Button} from '@ui/buttons/button';

import { FilterOperator } from '@common/datatable/filters/backend-filter';
import { useUpdateEffect } from '@react-aria/utils';

import { FilterListValue } from '@common/datatable/filters/utils/encode-backend-filters';
import { decodeBackendFilters } from '@common/datatable/filters/utils/decode-backend-filters';

import { useBackendFilterUrlParams } from '@common/datatable/filters/backend-filter-url-params';
import { useSearchParams } from 'react-router';

import clsx from 'clsx';
import { MediaChevronDownIcon } from '@ui/icons/media/media-chevron-down';

import debounce from 'just-debounce-it';
import { useIsMobileMediaQuery } from '@ui/utils/hooks/is-mobile-media-query';

export interface ArtistFilterProps {
    type: string
    channel: Channel
}

export const ArtistFilter: FC<ArtistFilterProps> = ({ channel, type }) => {
    const [searchParams, setSearchParams] = useSearchParams()
    const [search, setSearch] = useState<string>(searchParams.get('query') || '')

    const [genres, setGenres] = useState<NormalizedModel[]>(channel?.slug === 'genre' && channel.restriction ? [channel.restriction] : [])

    const isMobile = useIsMobileMediaQuery()
    const { replaceAll, encodedFilters } = useBackendFilterUrlParams()

    const debouncedSetSearchParams = useCallback(
        debounce((value: string) => {
            const newSearchParams = new URLSearchParams(searchParams);
            newSearchParams.set('query', value);
            setSearchParams(newSearchParams);
        }, 200),
        [searchParams, setSearchParams]
    )

    const filters = useMemo(() => {
        if (!encodedFilters)
            return []

        return decodeBackendFilters(encodedFilters)
    }, [])
    const hasFilters = useMemo(() => {
        return genres.length > 0 && channel?.config?.restriction !== 'genre'
    }, [filters, genres, channel])

    const handleFilters = () => {
        const filters: FilterListValue[] = []

        if (genres?.length > 0 && channel?.restriction?.model_type !== 'genre') {
            filters.push({
                key: 'genres',
                operator: FilterOperator.has,
                value: genres
            })
        }

        replaceAll(filters)
    }

    const clearAll = () => {
        if (channel?.config?.restriction !== 'genre')
            setGenres([])

        replaceAll([])
    }

    // Search params are now updated with debounce in the onChange handler

    useUpdateEffect(() => {
        handleFilters()
    }, [genres])

    useEffect(() => {
        if (filters?.length < 1)
            return

        for (const filter of filters) {
            if (filter.key === 'genres' && filter.operator === FilterOperator.has) {
                setGenres(filter.value)
            }
        }
    }, [])

    return (
        <div className="flex flex-col w-full md:flex-row items-start gap-5 md:gap-10">
            <div className="flex flex-wrap items-center gap-5 md:gap-10">
                <TextField
                    name="search"
                    placeholder="start typing to search..."
                    inputClassName="!rounded-md text-xs !h-32 py-4 !min-w-400"
                    value={search}
                    onChange={e => {
                        const value = e.target.value;
                        setSearch(value);
                        debouncedSetSearchParams(value);
                    }}
                />

                {type !== 'tag' && (
                    <DialogTrigger
                        type="popover"
                        triggerOnHover
                        mobileType="modal"
                        returnFocusToTrigger={!isMobile}
                        moveFocusToDialog={!isMobile}
                    >
                        <button
                            className={clsx(
                                'flex items-center gap-10 text-muted bg-hover/5 hover:bg-hover text-xs py-4 rounded-md pl-10 h-32',
                                {
                                    'pr-10': !genres.length,
                                    'pr-4': genres.length
                                }
                            )}
                        >
                            <GenresIcon className="w-16 h-16" />
                            <span>Genres</span>

                            <MediaChevronDownIcon className="w-12 h-12" />

                            {genres?.length > 0 && (
                                <>
                                    <span className="h-20 w-1 bg-background/70"></span>
                                    <span className="block text-xs text-muted py-4 px-8 rounded bg-background/50">{genres[0]?.display_name || genres[0]?.name}</span>
                                    {genres?.length > 1 && <span className="block text-xs text-muted py-4 px-8 rounded bg-background/50">+{genres.length - 1}</span>}
                                </>
                            )}
                        </button>

                        <GenresList
                            selected={genres}
                            onApply={(list) => setGenres(list)}
                            onClear={() => setGenres([])}
                        />
                    </DialogTrigger>
                )}

                {hasFilters && (
                    <Button
                        size="xs"
                        variant="flat"
                        onClick={() => clearAll()}
                        radius="rounded-md"
                        className="text-muted !h-32 hover:text-main"
                    >
                        Clear all
                    </Button>
                )}
            </div>
        </div>
    )
}
